import React from 'react';
import { DollarSign, AlertTriangle, TrendingUp, Target, Star } from 'lucide-react';
import { getRetailerInfo } from '../config/retailers';

interface Insight {
  id: string;
  insight_type: string;
  category: string;
  priority: string;
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number;
  confidence_score: number;
  source: string;
  retailer_id?: string;
  metadata?: {
    model_name?: string;
    model_accuracy?: string;
  };
}

interface CompactInsightCardProps {
  insight: Insight;
  onClick?: () => void;
}

export function CompactInsightCard({ insight, onClick }: CompactInsightCardProps) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'achievement': return Star;  // NEW: Celebratory icon for wins
      case 'risk': return AlertTriangle;
      case 'opportunity': return TrendingUp;
      case 'alert': return AlertTriangle;
      default: return Target;
    }
  };

  const getPriorityColor = (priority: string, insightType: string) => {
    // Achievements get green treatment regardless of priority
    if (insightType === 'achievement') {
      return 'border-l-green-500 bg-green-50/50 hover:bg-green-50';
    }
    
    switch (priority) {
      case 'critical': return 'border-l-red-500 bg-red-50/50 hover:bg-red-50';
      case 'high': return 'border-l-orange-500 bg-orange-50/50 hover:bg-orange-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50/50 hover:bg-yellow-50';
      default: return 'border-l-blue-500 bg-blue-50/50 hover:bg-blue-50';
    }
  };

  const getPriorityBadge = (priority: string, insightType: string) => {
    // Achievements show a green "STAR" badge instead of priority
    if (insightType === 'achievement') {
      return <span className="px-1.5 py-0.5 text-[10px] font-bold rounded border bg-green-100 text-green-800 border-green-300">⭐ WIN</span>;
    }
    
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300',
    };
    return <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${colors[priority] || colors.low}`}>{priority.toUpperCase()}</span>;
  };

  const getRetailerBadge = (retailerId?: string) => {
    if (!retailerId) return null;
    
    const retailerInfo = getRetailerInfo(retailerId);
    if (!retailerInfo) return null;

    return (
      <div 
        className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded border"
        style={{ 
          backgroundColor: retailerInfo.colors.light,
          borderColor: retailerInfo.colors.medium,
          color: retailerInfo.colors.dark || retailerInfo.colors.primary
        }}
      >
        <span>{retailerInfo.name}</span>
      </div>
    );
  };

  const TypeIcon = getTypeIcon(insight.insight_type);
  const isAchievement = insight.insight_type === 'achievement';

  return (
    <div
      className={`border-l-4 ${getPriorityColor(insight.priority, insight.insight_type)} border border-gray-200 rounded-lg p-3 cursor-pointer transition-all group`}
      onClick={onClick}
      title={insight.description}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Left: Icon + Badges + Title */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TypeIcon className={`w-4 h-4 flex-shrink-0 ${isAchievement ? 'text-green-600' : 'text-gray-600'}`} />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {getPriorityBadge(insight.priority, insight.insight_type)}
            {getRetailerBadge(insight.retailer_id)}
          </div>
          <p className="text-xs font-semibold text-gray-900 truncate group-hover:text-gray-700">
            {insight.title}
          </p>
        </div>

        {/* Right: Dollar Impact */}
        {insight.dollar_impact > 0 && (
          <div className="flex items-center gap-1 text-xs font-bold text-gray-900 flex-shrink-0">
            <DollarSign className="w-3 h-3" />
            {Math.round(insight.dollar_impact).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

export default CompactInsightCard;

